import {
  createInfiniteQuery,
  createQuery,
  createSuspenseQuery,
  type InfiniteQueryHookOptions,
} from 'react-query-kit';
import { type InfiniteData, keepPreviousData } from '@tanstack/react-query';

import type { QuizsetFolderCursorSearchReq } from '@/api/gen/_model/quizsetFolderCursorSearchReq';
import type { QuizsetFolderNextRes } from '@/api/gen/_model/quizsetFolderNextRes';
import {
  isRecoverableCursorError,
  useCursorQueryRecovery,
} from '@/shared/hooks/useCursorQueryRecovery';

import type { Folder } from '../types/folder.types';

import {
  fetchFolderDetail,
  fetchMyFolders,
  type FolderCursorPage,
  type FolderSearchPage,
  searchMyFolders,
} from './fetchers/folder';
import { fetchNextFolderQuiz } from './fetchers/folderPlay';
import { folderKeys } from './folder.keys';

export type MyFoldersVariables = Omit<QuizsetFolderCursorSearchReq, 'cursor'>;

type MyFoldersQueryOptions = InfiniteQueryHookOptions<
  FolderSearchPage,
  Error,
  InfiniteData<FolderSearchPage, string | undefined>,
  MyFoldersVariables,
  string | undefined
>;

/**
 * 원본 쿼리. 키·옵션을 직접 다뤄야 하는 곳(테스트 등)만 쓰고, 화면은 아래 `useMyFolders` 를 쓴다.
 */
export const useMyFoldersQuery = createInfiniteQuery<
  FolderSearchPage,
  MyFoldersVariables,
  Error,
  string | undefined
>({
  queryKey: folderKeys.listSearch(),
  fetcher: (variables, { pageParam, signal }) =>
    searchMyFolders(variables, pageParam, signal),
  getNextPageParam: lastPage =>
    lastPage.hasNext ? lastPage.nextCursor : undefined,
  initialPageParam: undefined,
  placeholderData: keepPreviousData,
  throwOnError: error => !isRecoverableCursorError(error),
});

export const useMyFolders = (options?: MyFoldersQueryOptions) => {
  const query =
    useMyFoldersQuery<InfiniteData<FolderSearchPage, string | undefined>>(
      options,
    );
  const { isRecovering, shouldMaskError } = useCursorQueryRecovery({
    isError: query.isError,
    error: query.error,
    hasLoadedPages:
      !query.isPlaceholderData && (query.data?.pages.length ?? 0) > 0,
    refetch: query.refetch,
  });

  return {
    ...query,
    isError: query.isError && !shouldMaskError,
    isFetchingNextPage: query.isFetchingNextPage || isRecovering,
  };
};

export const useMyFoldersWithItems = createInfiniteQuery<
  FolderCursorPage,
  void,
  Error,
  string | undefined
>({
  queryKey: folderKeys.listWithItems(),
  fetcher: (_variables, { pageParam }) => fetchMyFolders(pageParam),
  getNextPageParam: lastPage => lastPage.nextCursor,
  initialPageParam: undefined,
});

/** 폴더 상세 쿼리의 공통 옵션. 일반·suspense 두 훅이 같은 캐시를 같은 규칙으로 읽는다. */
const FOLDER_DETAIL_QUERY_OPTIONS = {
  queryKey: folderKeys.details(),
  fetcher: ({ id }: { id: string }) => fetchFolderDetail(id),
  staleTime: 30_000,
  refetchOnWindowFocus: false,
} as const;

/**
 * 폴더 상세.
 *
 * suspense 쿼리가 아니다. SSR HTML 을 하이드레이션하는 동안 Suspense 경계 안에서 상태가 바뀌면
 * React 가 서버 HTML 을 버리고 다시 그린다.
 */
export const useFolderDetail = createQuery<Folder, { id: string }>(
  FOLDER_DETAIL_QUERY_OPTIONS,
);

/**
 * 같은 상세를 suspense 로 읽는다. 캐시 키가 같아 `useFolderDetail` 과 캐시를 공유한다.
 *
 * `NoSSR` 로 감싼 화면 전용이다(편집 화면과 퀴즈 추가 모달).
 * ⚠️ SSR 로 그리는 화면(폴더 공개 상세)에는 쓰지 말 것.
 */
export const useFolderDetailSuspense = createSuspenseQuery<
  Folder,
  { id: string }
>(FOLDER_DETAIL_QUERY_OPTIONS);

/**
 * 폴더 재생에서 다음 퀴즈를 판정한다.
 *
 * 서버가 재생 커서를 들고 있지 않아 지금 위치를 매번 함께 보낸다.
 * POST 지만 서버 상태를 바꾸지 않는 조회라 query 로 둔다.
 *
 * `viewerId` 는 요청에 싣지 않는다 — 로그인·로그아웃으로 사용자가 바뀌면 캐시가 갈리게 키에만 넣는다.
 */
export const useFolderNextQuiz = createQuery<
  QuizsetFolderNextRes,
  { folderId: string; currentQuizsetId: string; viewerId: string | null }
>({
  queryKey: folderKeys.playNext(),
  fetcher: ({ folderId, currentQuizsetId }) =>
    fetchNextFolderQuiz(folderId, currentQuizsetId),
  staleTime: 0,
  refetchOnWindowFocus: false,
});
